/**
 * Premium Slideshow - Advanced JavaScript Controller
 * 
 * Features:
 * - Smooth transitions with configurable easing
 * - Autoplay with progress indication
 * - Touch/swipe support
 * - Keyboard navigation
 * - Pause on hover/focus
 * - Visibility API support (pause when tab hidden)
 * - Intersection Observer (pause when out of view)
 * - Parallax effect on scroll
 * - ARIA accessibility
 * - Theme editor integration
 */

(function() {
  'use strict';

  // Custom element definition
  class PremiumSlideshow extends HTMLElement {
    constructor() {
      super();
      
      // Core elements
      this.slideshow = this.querySelector('.premium-slideshow');
      this.slides = Array.from(this.querySelectorAll('.premium-slide'));
      this.dots = Array.from(this.querySelectorAll('.premium-slideshow__dot'));
      this.counter = this.querySelector('.premium-slideshow__counter');
      this.counterCurrent = this.querySelector('.premium-slideshow__counter-current');
      this.prevBtn = this.querySelector('.premium-slideshow__nav--prev');
      this.nextBtn = this.querySelector('.premium-slideshow__nav--next');
      this.autoplayBtn = this.querySelector('.premium-slideshow__autoplay');
      
      // State
      this.currentIndex = 0;
      this.previousIndex = 0;
      this.slideCount = this.slides.length;
      this.isPlaying = false;
      this.isPaused = false;
      this.autoplayTimer = null;
      this.isInView = true;
      this.isHovered = false;
      this.isFocused = false;
      
      // Configuration
      this.config = {
        autoplay: this.slideshow?.dataset.autoplay === 'true',
        autoplaySpeed: parseInt(this.slideshow?.dataset.autoplaySpeed, 10) || 5000,
        pauseOnHover: this.slideshow?.dataset.pauseOnHover !== 'false',
        pauseOnFocus: true,
        parallax: this.slideshow?.classList.contains('premium-slideshow--parallax'),
        swipeThreshold: 50,
        transitionDuration: 800
      };
      
      // Touch tracking
      this.touch = {
        startX: 0,
        startY: 0,
        startTime: 0,
        isSwiping: false
      };
      
      // Bind methods
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleAutoplayToggle = this.handleAutoplayToggle.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleSlideClick = this.handleSlideClick.bind(this);
    }
    
    connectedCallback() {
      if (this.slideCount <= 0) return;
      
      this.init();
    }
    
    disconnectedCallback() {
      this.destroy();
    }
    
    init() {
      // Set CSS variable for autoplay speed (used by progress animation)
      this.slideshow.style.setProperty('--ps-autoplay-speed', `${this.config.autoplaySpeed}ms`);
      
      // Initialize first slide
      this.goToSlide(0, false);
      
      // Setup event listeners
      this.setupEventListeners();
      
      // Setup Intersection Observer
      this.setupIntersectionObserver();
      
      // Setup parallax if enabled
      if (this.config.parallax) {
        this.setupParallax();
      }
      
      // Start autoplay if enabled
      if (this.config.autoplay && this.slideCount > 1) {
        this.startAutoplay();
      }
      
      // Remove loading state
      this.slideshow.classList.remove('premium-slideshow--loading');
      
      // Announce to screen readers
      this.announceSlide();
    }
    
    destroy() {
      // Stop autoplay
      this.stopAutoplay();
      
      // Remove event listeners
      this.removeEventListeners();
      
      // Disconnect observers
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
      
      // Remove scroll listener for parallax
      if (this.config.parallax) {
        window.removeEventListener('scroll', this.handleScroll);
      }
    }
    
    setupEventListeners() {
      // Navigation buttons
      this.prevBtn?.addEventListener('click', this.handlePrev);
      this.nextBtn?.addEventListener('click', this.handleNext);
      
      // Pagination dots
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.handleDotClick(index));
      });
      
      // Autoplay toggle
      this.autoplayBtn?.addEventListener('click', this.handleAutoplayToggle);
      
      // Touch events
      this.slideshow.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.slideshow.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.slideshow.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      
      // Full-slide click (data-href)
      this.slideshow.addEventListener('click', this.handleSlideClick);
      
      // Keyboard navigation
      this.slideshow.addEventListener('keydown', this.handleKeydown);
      
      // Hover pause
      if (this.config.pauseOnHover) {
        this.slideshow.addEventListener('mouseenter', this.handleMouseEnter);
        this.slideshow.addEventListener('mouseleave', this.handleMouseLeave);
      }
      
      // Focus pause
      if (this.config.pauseOnFocus) {
        this.slideshow.addEventListener('focusin', this.handleFocusIn);
        this.slideshow.addEventListener('focusout', this.handleFocusOut);
      }
      
      // Visibility change
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    
    removeEventListeners() {
      this.prevBtn?.removeEventListener('click', this.handlePrev);
      this.nextBtn?.removeEventListener('click', this.handleNext);
      this.autoplayBtn?.removeEventListener('click', this.handleAutoplayToggle);
      
      this.slideshow.removeEventListener('touchstart', this.handleTouchStart);
      this.slideshow.removeEventListener('touchmove', this.handleTouchMove);
      this.slideshow.removeEventListener('touchend', this.handleTouchEnd);
      this.slideshow.removeEventListener('click', this.handleSlideClick);
      this.slideshow.removeEventListener('keydown', this.handleKeydown);
      this.slideshow.removeEventListener('mouseenter', this.handleMouseEnter);
      this.slideshow.removeEventListener('mouseleave', this.handleMouseLeave);
      this.slideshow.removeEventListener('focusin', this.handleFocusIn);
      this.slideshow.removeEventListener('focusout', this.handleFocusOut);
      
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;
      
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        root: null,
        rootMargin: '0px',
        threshold: 0.3
      });
      
      this.intersectionObserver.observe(this.slideshow);
    }
    
    setupParallax() {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.handleScroll(); // Initial call
    }
    
    // ==========================================================================
    // Navigation Methods
    // ==========================================================================
    
    goToSlide(index, animate = true) {
      if (index === this.currentIndex && animate) return;
      if (index < 0) index = this.slideCount - 1;
      if (index >= this.slideCount) index = 0;
      
      this.previousIndex = this.currentIndex;
      this.currentIndex = index;
      
      // Update slides
      this.slides.forEach((slide, i) => {
        slide.classList.remove('is-active', 'is-leaving');
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
        
        if (i === this.previousIndex && animate) {
          slide.classList.add('is-leaving');
          // Remove is-leaving after transition
          setTimeout(() => {
            slide.classList.remove('is-leaving');
          }, this.config.transitionDuration);
        }
        
        if (i === this.currentIndex) {
          slide.classList.add('is-active');
          slide.setAttribute('aria-hidden', 'false');
          slide.removeAttribute('tabindex');
        }
      });
      
      // Update dots
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
        dot.setAttribute('aria-selected', i === this.currentIndex);
      });
      
      // Update counter
      if (this.counterCurrent) {
        this.counterCurrent.textContent = this.currentIndex + 1;
      }
      
      // Restart autoplay timer
      if (this.isPlaying && !this.isPaused) {
        this.restartAutoplayTimer();
      }
      
      // Announce to screen readers
      if (animate) {
        this.announceSlide();
      }
      
      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('slideChange', {
        detail: {
          currentIndex: this.currentIndex,
          previousIndex: this.previousIndex,
          slide: this.slides[this.currentIndex]
        }
      }));
    }
    
    next() {
      this.goToSlide(this.currentIndex + 1);
    }
    
    prev() {
      this.goToSlide(this.currentIndex - 1);
    }
    
    // ==========================================================================
    // Autoplay Methods
    // ==========================================================================
    
    startAutoplay() {
      this.isPlaying = true;
      this.isPaused = false;
      this.slideshow.classList.remove('is-paused');
      this.restartAutoplayTimer();
    }
    
    stopAutoplay() {
      this.isPlaying = false;
      this.clearAutoplayTimer();
      this.slideshow.classList.add('is-paused');
    }
    
    pauseAutoplay() {
      if (!this.isPlaying) return;
      this.isPaused = true;
      this.clearAutoplayTimer();
      this.slideshow.classList.add('is-paused');
    }
    
    resumeAutoplay() {
      if (!this.isPlaying) return;
      this.isPaused = false;
      this.slideshow.classList.remove('is-paused');
      this.restartAutoplayTimer();
    }
    
    toggleAutoplay() {
      if (this.isPlaying) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }
    
    restartAutoplayTimer() {
      this.clearAutoplayTimer();
      this.autoplayTimer = setTimeout(() => {
        this.next();
      }, this.config.autoplaySpeed);
    }
    
    clearAutoplayTimer() {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
    
    // ==========================================================================
    // Event Handlers
    // ==========================================================================
    
    handlePrev(e) {
      e?.preventDefault();
      this.prev();
    }
    
    handleNext(e) {
      e?.preventDefault();
      this.next();
    }
    
    handleDotClick(index) {
      this.goToSlide(index);
    }
    
    handleAutoplayToggle(e) {
      e?.preventDefault();
      this.toggleAutoplay();
    }
    
    handleTouchStart(e) {
      if (this.slideCount <= 1) return;
      
      this.touch.startX = e.touches[0].clientX;
      this.touch.startY = e.touches[0].clientY;
      this.touch.startTime = Date.now();
      this.touch.isSwiping = false;
    }
    
    handleTouchMove(e) {
      if (this.slideCount <= 1) return;
      
      const deltaX = e.touches[0].clientX - this.touch.startX;
      const deltaY = e.touches[0].clientY - this.touch.startY;
      
      // Determine if horizontal swipe
      if (!this.touch.isSwiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this.touch.isSwiping = true;
      }
      
      // Prevent vertical scroll during horizontal swipe
      if (this.touch.isSwiping) {
        e.preventDefault();
      }
    }
    
    handleTouchEnd(e) {
      if (this.slideCount <= 1 || !this.touch.isSwiping) return;
      
      const deltaX = e.changedTouches[0].clientX - this.touch.startX;
      const deltaTime = Date.now() - this.touch.startTime;
      const velocity = Math.abs(deltaX) / deltaTime;
      
      // Check if swipe was fast enough or long enough
      const isValidSwipe = Math.abs(deltaX) > this.config.swipeThreshold || velocity > 0.5;
      
      if (isValidSwipe) {
        if (deltaX > 0) {
          this.prev();
        } else {
          this.next();
        }
      }
      
      this.touch.isSwiping = false;
    }
    
    handleKeydown(e) {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.next();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.slideCount - 1);
          break;
        case ' ':
        case 'Enter':
          if (e.target === this.slideshow || e.target.classList.contains('premium-slideshow__autoplay')) {
            e.preventDefault();
            this.toggleAutoplay();
          }
          break;
      }
    }
    
    handleVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.isInView && !this.isHovered && !this.isFocused) {
        this.resumeAutoplay();
      }
    }
    
    handleIntersection(entries) {
      entries.forEach(entry => {
        this.isInView = entry.isIntersecting;
        
        if (!entry.isIntersecting) {
          this.pauseAutoplay();
        } else if (!document.hidden && !this.isHovered && !this.isFocused) {
          this.resumeAutoplay();
        }
      });
    }
    
    handleMouseEnter() {
      this.isHovered = true;
      this.pauseAutoplay();
    }
    
    handleMouseLeave() {
      this.isHovered = false;
      if (this.isInView && !document.hidden && !this.isFocused) {
        this.resumeAutoplay();
      }
    }
    
    handleFocusIn() {
      this.isFocused = true;
      this.pauseAutoplay();
    }
    
    handleFocusOut(e) {
      // Check if focus moved outside the slideshow
      if (!this.slideshow.contains(e.relatedTarget)) {
        this.isFocused = false;
        if (this.isInView && !document.hidden && !this.isHovered) {
          this.resumeAutoplay();
        }
      }
    }
    
    handleSlideClick(e) {
      // Don't navigate if clicking on interactive elements
      const interactive = e.target.closest('a, button, .premium-slideshow__nav, .premium-slideshow__pagination, .premium-slideshow__autoplay, .premium-slideshow__counter');
      if (interactive) return;
      
      // Navigate to the active slide's data-href URL
      const activeSlide = this.slides[this.currentIndex];
      if (!activeSlide) return;
      
      const href = activeSlide.dataset.href;
      if (href) {
        window.location.href = href;
      }
    }
    
    handleScroll() {
      if (!this.config.parallax) return;
      
      const rect = this.slideshow.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Only apply parallax when slideshow is visible
      if (rect.bottom < 0 || rect.top > windowHeight) return;
      
      // Calculate parallax offset
      const scrollPercent = (windowHeight - rect.top) / (windowHeight + rect.height);
      const parallaxOffset = (scrollPercent - 0.5) * 40; // -20px to +20px
      
      // Apply to current slide's media
      const currentSlide = this.slides[this.currentIndex];
      const media = currentSlide?.querySelector('.premium-slide__media');
      
      if (media) {
        media.style.transform = `translateY(${parallaxOffset}px)`;
      }
    }
    
    // ==========================================================================
    // Accessibility
    // ==========================================================================
    
    announceSlide() {
      const slide = this.slides[this.currentIndex];
      const title = slide?.querySelector('.premium-slide__title')?.textContent || '';
      const announcement = `Slide ${this.currentIndex + 1} of ${this.slideCount}${title ? ': ' + title : ''}`;
      
      // Use live region if available
      let liveRegion = this.querySelector('[aria-live]');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.classList.add('visually-hidden');
        this.appendChild(liveRegion);
      }
      
      liveRegion.textContent = announcement;
    }
  }
  
  // Register custom element
  if (!customElements.get('premium-slideshow')) {
    customElements.define('premium-slideshow', PremiumSlideshow);
  }
  
  // ==========================================================================
  // Shopify Theme Editor Integration
  // ==========================================================================
  
  document.addEventListener('shopify:section:load', function(e) {
    const section = e.target;
    const slideshow = section.querySelector('premium-slideshow');
    
    if (slideshow) {
      // Force re-initialization by removing and re-adding
      const parent = slideshow.parentNode;
      const nextSibling = slideshow.nextSibling;
      parent.removeChild(slideshow);
      parent.insertBefore(slideshow, nextSibling);
    }
  });
  
  document.addEventListener('shopify:section:unload', function(e) {
    const section = e.target;
    const slideshow = section.querySelector('premium-slideshow');
    
    if (slideshow) {
      slideshow.destroy?.();
    }
  });
  
  document.addEventListener('shopify:block:select', function(e) {
    const section = e.target.closest('.shopify-section');
    const slideshow = section?.querySelector('premium-slideshow');
    const slideIndex = parseInt(e.target.dataset.index, 10);
    
    if (slideshow && !isNaN(slideIndex)) {
      slideshow.stopAutoplay?.();
      slideshow.goToSlide?.(slideIndex);
    }
  });
  
  document.addEventListener('shopify:block:deselect', function(e) {
    const section = e.target.closest('.shopify-section');
    const slideshow = section?.querySelector('premium-slideshow');
    
    if (slideshow && slideshow.config?.autoplay) {
      slideshow.startAutoplay?.();
    }
  });
  
})();
